import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowRight, ChevronLeft, Brain, Shield, Eye, Zap, Lock, Search, Layers } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useRegion } from '../../contexts/RegionContext';
import FozzilPayCheckout from '../FozzilPayCheckout';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};

const ShopIntelligence = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { region, formatPrice } = useRegion();
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutProduct, setCheckoutProduct] = useState({
    amount: String(region.intelligence.blacksitePayu),
    name: "T.A.E.L.S Blacksite",
    isRecurring: true
  });

  const handleBlacksiteSubscribe = () => {
    if (!user) {
      navigate("/auth", { state: { from: { pathname: '/shop/intelligence' } } });
      return;
    }
    setCheckoutProduct({
      amount: String(region.intelligence.blacksitePayu),
      name: "T.A.E.L.S Blacksite",
      isRecurring: true
    });
    setShowCheckout(true);
  };

  const handlePaymentSuccess = (data) => {
    console.log("Payment successful:", data);
    window.location.href = `https://taels.fozzil.in/payment-success?txnid=${data.txnid || ''}`;
  };

  const plans = [
    {
      id: 'basic', name: 'Basic', icon: Search,
      price: 'Free', priceNote: 'Always free',
      tagline: 'Your entry into the world of T.A.E.L.S.',
      features: ['Ask, Search, & Reasoning', 'Lightweight tasks', 'Voice command interface', 'Limited file uploads'],
      ctaLabel: 'Current Plan', ctaDisabled: true, isPopular: false,
    },
    {
      id: 'blacksite', name: 'Blacksite', icon: Brain,
      price: `${formatPrice(region.intelligence.blacksite)}`, priceSuffix: '/mo',
      priceNote: 'Exclusive of convenience fees',
      tagline: 'Next-generation intelligence, unlocked.',
      features: ['T.A.E.L.S APEX Thinking Engines', 'Deep Research Models', 'Stealth / Ghost Mode', 'Advanced Creative Suite', 'Ultra-Fast Multi-layered Reasoning', 'Full Privacy Masking'],
      ctaLabel: 'Subscribe Now', ctaDisabled: false, isPopular: true,
    },
    {
      id: 'extremis', name: 'Extremis', icon: Shield,
      price: 'Coming Soon', priceNote: 'The future awaits',
      tagline: 'Hyper-personalized private AI.',
      features: ['Dedicated AI Instance', 'Hyper-Personalization', 'Autonomous Operations', 'Full Data Privacy'],
      ctaLabel: 'Coming Soon', ctaDisabled: true, isPopular: false, isComingSoon: true,
    },
  ];

  return (
    <>
      <div className="store-page bg-white dark:bg-black text-[#1d1d1f] dark:text-[#f5f5f7] transition-colors duration-400 min-h-screen">
        {/* Breadcrumb */}
        <div className="max-w-[1120px] mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <Link to="/shop" className="inline-flex items-center gap-1 text-[#065DC0] text-xs sm:text-sm font-medium hover:underline">
            <ChevronLeft size={14} /> Back to Store
          </Link>
        </div>

        {/* Hero */}
        <section className="py-6 sm:py-8 md:py-12">
          <div className="max-w-[1120px] mx-auto px-4 sm:px-6">
            <motion.div initial="hidden" animate="visible" variants={stagger} className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 sm:gap-6">
              <div>
                <motion.h1 variants={fadeInUp} className="text-[clamp(28px,6vw,64px)] font-bold tracking-tight leading-none">Subscribe Intelligence</motion.h1>
                <motion.p variants={fadeInUp} className="text-[#6e6e73] dark:text-[#a1a1a6] text-sm sm:text-base md:text-lg font-light mt-2 sm:mt-3 max-w-lg">Choose the plan that powers your future.</motion.p>
              </div>
              <motion.div variants={fadeInUp}>
                <Link to="/taels" className="text-[#065DC0] text-xs sm:text-sm font-medium inline-flex items-center gap-1 hover:underline">Learn more <ArrowRight size={14} /></Link>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Product Hero Banner */}
        <section className="py-4 sm:py-6 md:py-10">
          <div className="max-w-[1120px] mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="relative bg-gradient-to-br from-[#1e3a8a] via-[#3b82f6] to-[#7c3aed] dark:from-[#0f172a] dark:via-[#1e40af] dark:to-[#4c1d95] rounded-[20px] sm:rounded-[32px] md:rounded-[44px] p-5 sm:p-8 md:p-14 overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.04] to-transparent" />
              <div className="absolute top-0 right-0 w-[200px] sm:w-[400px] h-[200px] sm:h-[400px] bg-blue-400/10 blur-[120px] rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="relative z-10 flex flex-col sm:flex-row items-center gap-4 sm:gap-6 md:gap-8">
                <div className="flex-1">
                  <span className="text-[10px] sm:text-xs font-bold text-white/60 uppercase tracking-[0.3em] mb-2 sm:mb-3 block">AI Platform</span>
                  <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white tracking-tight mb-2 sm:mb-4">T.A.E.L.S</h2>
                  <p className="text-white/80 text-xs sm:text-sm md:text-base leading-relaxed max-w-md mb-4 sm:mb-6">Multi-modal AI with thinking engines, deep research, stealth mode, and advanced creative capabilities.</p>
                  <div className="flex gap-2 sm:gap-3 flex-wrap">
                    <button onClick={handleBlacksiteSubscribe} className="px-4 sm:px-6 py-2.5 sm:py-3 rounded-full bg-white text-[#1e3a8a] text-xs sm:text-sm font-bold hover:bg-white/90 transition-all inline-flex items-center gap-1.5 sm:gap-2 cursor-pointer border-none">
                      Subscribe Blacksite <ArrowRight size={14} />
                    </button>
                    <Link to="/taels" className="px-4 sm:px-6 py-2.5 sm:py-3 rounded-full bg-transparent text-white text-xs sm:text-sm font-semibold border border-white/30 hover:bg-white/10 transition-all inline-flex items-center gap-2">Explore</Link>
                  </div>
                </div>
                <div className="w-[100px] sm:w-[160px] md:w-[220px] shrink-0 aspect-square bg-black rounded-[24px] sm:rounded-[32px] overflow-hidden flex items-center justify-center p-0">
                  <img src={process.env.PUBLIC_URL + '/TAELS Logo black bg.png'} alt="T.A.E.L.S" className="w-full h-full object-cover" />
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* All Plans - Ecosystem Card Style Compact */}
        <section className="py-8 sm:py-12 md:py-16">
          <div className="max-w-[1120px] mx-auto px-4 sm:px-6">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-50px' }} variants={stagger}>
              <motion.h2 variants={fadeInUp} className="text-[clamp(20px,3.5vw,36px)] font-bold tracking-tight mb-5 sm:mb-8">
                All plans. <span className="text-[#065DC0]/80 dark:text-blue-400/80 font-medium">Upgrade your intelligence.</span>
              </motion.h2>

              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5">
                {plans.map((plan) => (
                    <motion.div key={plan.id} variants={fadeInUp}>
                      <div
                        className={`relative group p-4 sm:p-5 md:p-7 rounded-[20px] sm:rounded-[28px] md:rounded-[32px] bg-white dark:bg-zinc-950 border transition-all duration-700 overflow-hidden h-full flex flex-col ${
                          plan.isPopular
                            ? 'border-blue-500 shadow-[0_32px_64px_-16px_rgba(6,93,192,0.2)]'
                            : plan.isComingSoon
                            ? 'border-dashed border-black/[0.08] dark:border-white/[0.08] opacity-70 hover:opacity-100'
                            : 'border-black/[0.08] dark:border-white/[0.08] hover:border-blue-500/30 shadow-sm dark:shadow-none'
                        }`}
                      >
                        {/* Artistic Background Elements */}
                        <div className="absolute inset-0 bg-gradient-to-b from-blue-500/[0.02] to-transparent pointer-events-none" />
                        <div className="absolute -top-12 -right-12 w-32 h-32 bg-[#065DC0]/5 blur-[40px] rounded-full pointer-events-none" />
                        {/* Blue Hover Glow */}
                        <div className="absolute top-0 right-0 w-24 sm:w-32 h-24 sm:h-32 bg-blue-600/10 blur-[60px] opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />

                        {plan.isPopular && (
                          <div className="absolute top-3 right-3 sm:top-4 sm:right-4 md:top-6 md:right-6 z-30 bg-blue-500/10 text-blue-600 dark:text-blue-400 text-[8px] sm:text-[10px] uppercase font-black px-2 sm:px-3 py-0.5 sm:py-1 rounded-full border border-blue-500/20">Most Advanced</div>
                        )}

                        {/* Logo instead of Icon */}
                        <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-[32px] flex items-center justify-center mb-3 sm:mb-4 md:mb-5 bg-black border border-black/[0.06] dark:border-white/[0.1] group-hover:scale-110 transition-transform duration-700 relative overflow-hidden p-0">
                          <div className="absolute inset-0 bg-blue-500/5 dark:bg-blue-400/10 pointer-events-none" />
                          <img src={process.env.PUBLIC_URL + '/TAELS Logo black bg.png'} alt="T.A.E.L.S" className="w-full h-full object-cover shadow-2xl" />
                        </div>

                        <h3 className="text-base sm:text-lg md:text-2xl font-bold tracking-tighter text-[#1d1d1f] dark:text-white mb-0.5 sm:mb-1 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors uppercase">{plan.name}</h3>
                        <div className="flex items-end gap-0.5 sm:gap-1 mb-0.5 sm:mb-1">
                          <span className="text-lg sm:text-2xl md:text-3xl font-bold text-[#1d1d1f] dark:text-white">{plan.price}</span>
                          {plan.priceSuffix && <span className="text-[10px] sm:text-xs text-[#6e6e73] mb-0.5 font-medium">{plan.priceSuffix}</span>}
                        </div>
                        {plan.priceNote && <p className="text-[9px] sm:text-[10px] md:text-[11px] text-[#86868b] dark:text-[#6e6e73] mb-2 sm:mb-3">{plan.priceNote}</p>}
                        <p className="text-[10px] sm:text-xs md:text-sm font-light text-[#6e6e73] dark:text-zinc-500 mb-3 sm:mb-4 leading-relaxed">{plan.tagline}</p>

                        {/* Features List - compact */}
                        <div className="flex flex-col mb-3 sm:mb-4 md:mb-5 w-full flex-1">
                          {plan.features.map((feat, i) => (
                            <div key={i} className="flex items-center gap-2 sm:gap-3 py-1.5 sm:py-2 md:py-2.5 border-b border-black/[0.04] dark:border-white/5 text-[10px] sm:text-[11px] md:text-[13px]">
                              <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-blue-500 shrink-0" />
                              <span className="font-medium text-[#1d1d1f] dark:text-zinc-300">{feat}</span>
                            </div>
                          ))}
                        </div>

                        {/* CTA - Single Line Colored Buttons */}
                        <div className="flex items-center gap-1.5 sm:gap-2 mt-auto pt-3 md:pt-4 border-t border-black/[0.06] dark:border-white/5 w-full">
                          <button
                            onClick={() => { if (plan.id === 'blacksite') handleBlacksiteSubscribe(); }}
                            disabled={plan.ctaDisabled}
                            className={`flex-1 py-1.5 sm:py-2 md:py-2.5 rounded-full text-white font-bold text-[8px] sm:text-[9px] md:text-xs uppercase tracking-wider transition-all border-none flex items-center justify-center ${
                              plan.ctaDisabled
                                ? 'bg-[#f5f5f7] dark:bg-[#2c2c2e] text-[#86868b] dark:text-[#6e6e73] opacity-60 cursor-not-allowed'
                                : plan.isPopular
                                ? 'bg-blue-600 hover:bg-blue-700 cursor-pointer'
                                : 'bg-[#065DC0] hover:bg-[#0077ed] cursor-pointer'
                            }`}
                          >
                            {plan.ctaLabel}
                          </button>
                          {!plan.isComingSoon && (
                            <Link
                              to="/taels"
                              className={`flex-1 py-1.5 sm:py-2 md:py-2.5 rounded-full bg-transparent font-bold text-[8px] sm:text-[9px] md:text-xs uppercase tracking-wider transition-all border flex items-center justify-center text-center dark:hover:text-white ${
                                plan.isPopular
                                  ? 'border-blue-600 text-blue-600 hover:bg-blue-600'
                                  : 'border-[#065DC0] text-[#065DC0] hover:bg-[#065DC0]'
                              }`}
                            >
                              Learn more
                            </Link>
                          )}
                        </div>
                      </div>
                    </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Intelligence Showcase */}
        <section className="py-8 sm:py-12 md:py-16">
          <div className="max-w-[1120px] mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="relative bg-white dark:bg-[#050505] rounded-[20px] sm:rounded-[32px] md:rounded-[44px] p-5 sm:p-8 md:p-14 overflow-hidden border border-black/[0.08] dark:border-white/[0.08] shadow-sm dark:shadow-[0_32px_64px_-16px_rgba(0,0,0,0.6)]"
            >
              {/* Creative Background Elements */}
              <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-500/10 blur-[130px] rounded-full -mr-72 -mt-72 pointer-events-none" />
              <div className="absolute top-1/2 left-0 w-[300px] h-[300px] bg-[#065DC0]/5 blur-[90px] rounded-full -ml-32 -translate-y-1/2 pointer-events-none" />

              <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 md:gap-8 relative z-10">
                <div className="flex-1">
                  <h3 className="text-lg sm:text-2xl md:text-3xl font-bold tracking-tight mb-2 sm:mb-4 text-[#1d1d1f] dark:text-white">Experience T.A.E.L.S.</h3>
                  <p className="text-xs sm:text-sm text-[#6e6e73] dark:text-[#a1a1a6] leading-relaxed mb-4 sm:mb-6">Multi-modal reasoning, deep research, creative generation, and privacy-first design.</p>
                  <Link to="/taels" className="text-[#065DC0] text-xs sm:text-sm font-semibold inline-flex items-center gap-1 hover:underline">Explore all capabilities <ArrowRight size={14} /></Link>
                </div>
                <div className="w-full sm:w-[280px] md:w-[400px] shrink-0 rounded-[16px] sm:rounded-[20px] overflow-hidden">
                  <img src={process.env.PUBLIC_URL + '/fozzil-intelligence-image2.png'} alt="T.A.E.L.S" className="w-full h-auto object-cover" />
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="text-center text-[9px] sm:text-[10px] md:text-xs text-[#86868b] dark:text-[#6e6e73] px-4 py-8 sm:py-12 max-w-2xl mx-auto uppercase tracking-[0.1em]">
          By proceeding, you agree to FOZZIL's
          <a href="/terms" target="_blank" rel="noopener noreferrer" className="mx-1 sm:mx-2 text-[#6e6e73] dark:text-[#a1a1a6] hover:text-blue-500 transition-colors">Terms</a>
          &
          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="ml-1 sm:ml-2 text-[#6e6e73] dark:text-[#a1a1a6] hover:text-blue-500 transition-colors">Privacy</a>.
        </footer>
      </div>

      {/* FozzilPay Checkout Modal */}
      <FozzilPayCheckout
        isOpen={showCheckout}
        onClose={() => setShowCheckout(false)}
        amount={checkoutProduct.amount}
        productinfo={checkoutProduct.name}
        isRecurring={checkoutProduct.isRecurring}
        user_id={user?.id || 'guest'}
        firstname={user?.user_metadata?.full_name || user?.email?.split('@')[0] || ''}
        email={user?.email || ''}
        onSuccess={handlePaymentSuccess}
        allowedMethods={['UPI', 'Card', 'Netbanking', 'Wallet']}
      />
    </>
  );
};

export default ShopIntelligence;
